import { createAsyncThunk } from "@reduxjs/toolkit";
import { setLoading } from "../../Slice/loading/loadingSlice";
import authApi from "../../authApi";
import {
  setHolidayEvents,
  setFilters,
  setError,
} from "../../Slice/holidayCalendarModule/HolidayPlannerSlice";
import { showSnackbar } from "../../Slice/snackbar/snackbarSlice";

/**
 * Helper: handle success responses
 */
const handleSuccess = (dispatch, response, successMsg) => {
  if (response?.status === 200) {
    dispatch(setHolidayEvents(response?.data?.events || []));
    dispatch(setError([]));
    if (successMsg) {
      dispatch(showSnackbar({ message: successMsg, type: "success" }));
    }
  }
};

/**
 * Helper: handle errors consistently
 */
const handleError = (dispatch, error, defaultMsg) => {
  const errorMsg = error?.response?.data?.msg || defaultMsg;
  dispatch(setError([errorMsg]));
  dispatch(showSnackbar({ message: errorMsg, type: "error" }));
  console.error("API Error:", errorMsg, error);
};

/** Fetch all holiday events */
export const holidayEventsList = createAsyncThunk(
  "holidayPlanner/holidayEventsList",
  async (_, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      const response = await authApi.get(`/absences/calendar-view`);
      handleSuccess(dispatch, response);
    } catch (error) {
      handleError(dispatch, error, "Error fetching holiday events");
    } finally {
      dispatch(setLoading(false));
    }
  }
);

/** Create absence */
export const createHolidayAbsence = createAsyncThunk(
  "holidayPlanner/createHolidayAbsence",
  async (payload, { dispatch }) => {
    try {
      const response = await authApi.post(`/absences`, payload);
      if (response?.status === 200) {
        dispatch(holidayEventsList()); // refresh
        dispatch(setError([]));
        dispatch(
          showSnackbar({
            message: response?.data?.msg || "Successfully Created",
            type: "success",
          })
        );
      }
    } catch (error) {
      handleError(dispatch, error, "Error creating absence");
    }
  }
);

/** Update absence */
export const updateHolidayAbsence = createAsyncThunk(
  "holidayPlanner/updateHolidayAbsence",
  async ({ id, payload }, { dispatch }) => {
    try {
      const response = await authApi.put(`/absences/${id}`, payload);
      if (response?.status === 200) {
        dispatch(holidayEventsList()); // refresh
        dispatch(setError([]));
        dispatch(
          showSnackbar({
            message: response?.data?.msg || "Successfully Updated",
            type: "success",
          })
        );
      }
    } catch (error) {
      handleError(dispatch, error, "Error updating absence");
    }
  }
);

/** Delete absence */
export const deleteHolidayAbsence = createAsyncThunk(
  "holidayPlanner/deleteHolidayAbsence",
  async (id, { dispatch }) => {
    try {
      const response = await authApi.delete(`/absences/${id}`);
      if (response?.status === 200) {
        dispatch(holidayEventsList()); // refresh
        dispatch(setError([]));
        dispatch(
          showSnackbar({
            message: response?.data?.msg || "Successfully Deleted",
            type: "success",
          })
        );
      }
    } catch (error) {
      handleError(dispatch, error, "Error deleting absence");
    }
  }
);

/** Update local filters (no API call) */
export const updateHolidayFilters = createAsyncThunk(
  "holidayPlanner/updateHolidayFilters",
  async (filterUpdates, { dispatch }) => {
    try {
      dispatch(setFilters(filterUpdates));
      dispatch(setError([]));
    } catch (error) {
      handleError(dispatch, error, "Error updating filters");
    }
  }
);

/** Apply filters (calls API with query params) */
export const applyHolidayFilters = createAsyncThunk(
  "holidayPlanner/applyHolidayFilters",
  async (_, { dispatch, getState }) => {
    try {
      dispatch(setLoading(true));

      const { filters } = getState().root.HolidayPlannerModule;
      const params = {};

      // Date range
      if (filters.startDate) params.from = filters.startDate.split("T")[0];
      if (filters.endDate) params.to = filters.endDate.split("T")[0];

      // Status filters
      if (filters.showAbsentOnly) params.status = "ABSENT";
      if (filters.showPresentOnly) {
        params.status = "FULL,HALF_AM,HALF_PM,LIMITED"; // all except ABSENT
      }

      // Language + linguist filters
      if (filters.sourceLanguage) params.source_lang = filters.sourceLanguage;
      if (filters.targetLanguage) params.target_lang = filters.targetLanguage;
      if (filters.resource) params.user_id = filters.resource;
      if (filters.status) params.status = filters.status;

      const response = await authApi.get(`/absences/calendar-view`, { params });
      handleSuccess(dispatch, response);
    } catch (error) {
      handleError(dispatch, error, "Error applying filters");
    } finally {
      dispatch(setLoading(false));
    }
  }
);
